import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, LogOut, Bike as BikeIcon, Images, X, Upload, MessageSquareQuote, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API, STORE, CATEGORIES, resolveImg } from "@/lib/store";

const EMPTY_BIKE = { name: "", category: "mountain-bikes", spec: "", description: "", price_display: "" };

function BikeForm({ initial, onClose, onSaved, authAxios }) {
  const [form, setForm] = useState(initial || EMPTY_BIKE);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial?.id);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("category", form.category);
      fd.append("spec", form.spec || "");
      fd.append("description", form.description || "");
      fd.append("price_display", form.price_display || "");
      fd.append("image_url", form.image_url || "");
      if (file) fd.append("image", file);
      const ax = authAxios();
      if (editing) await ax.put(`/admin/bikes/${initial.id}`, fd);
      else await ax.post(`/admin/bikes`, fd);
      toast.success(editing ? "Bike updated" : "Bike added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Could not save bike", { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">{editing ? "Edit Bike" : "Add New Bike"}</h3>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-zinc-100"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="bike-form">
          <div>
            <label className="mb-1 block text-sm font-medium">Name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]" data-testid="bike-form-name" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Category *</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]" data-testid="bike-form-category">
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Short spec line</label>
            <input value={form.spec} onChange={(e) => setForm({ ...form, spec: e.target.value })} placeholder="e.g. 21-speed Shimano, disc brakes" className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Price / label</label>
            <input value={form.price_display} onChange={(e) => setForm({ ...form, price_display: e.target.value })} placeholder="e.g. ₦450,000 or Enquire for price" className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Photo {editing ? "(leave blank to keep current)" : ""}</label>
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} className="w-full text-sm" data-testid="bike-form-image" />
            {editing && form.image_url && !file && <img src={resolveImg(form.image_url)} alt="current" className="mt-2 h-20 w-20 rounded-lg object-cover" />}
          </div>
          <button type="submit" disabled={saving} className="btn-brown flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-bold disabled:opacity-60" data-testid="bike-form-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save Changes" : "Add Bike"}
          </button>
        </form>
      </div>
    </div>
  );
}

const EMPTY_REVIEW = { name: "", location: "", quote: "", rating: 5 };

function TestimonialForm({ initial, onClose, onSaved, authAxios }) {
  const [form, setForm] = useState(initial || EMPTY_REVIEW);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial?.id);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: form.name, location: form.location || "", quote: form.quote, rating: Number(form.rating) || 5 };
      const ax = authAxios();
      if (editing) await ax.put(`/admin/testimonials/${initial.id}`, body);
      else await ax.post(`/admin/testimonials`, body);
      toast.success(editing ? "Testimonial updated" : "Testimonial added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Could not save", { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">{editing ? "Edit Testimonial" : "Add Testimonial"}</h3>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-zinc-100"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="review-form">
          <div>
            <label className="mb-1 block text-sm font-medium">Customer name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]" data-testid="review-form-name" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Location</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Ikeja, Lagos" className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Quote *</label>
            <textarea required rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]" data-testid="review-form-quote" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Rating</label>
            <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="w-full rounded-lg border border-[#E6D7C8] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#8C5A32]">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
            </select>
          </div>
          <button type="submit" disabled={saving} className="btn-brown flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-bold disabled:opacity-60" data-testid="review-form-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save Changes" : "Add Testimonial"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bikes");
  const [bikes, setBikes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editBike, setEditBike] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [editReview, setEditReview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    document.title = "Admin Dashboard | Knoble Rides";
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  const load = async () => {
    const [b, g, t] = await Promise.all([
      authAxios().get(`/bikes`),
      authAxios().get(`/gallery`),
      authAxios().get(`/testimonials`),
    ]);
    setBikes(b.data);
    setGallery(g.data);
    setTestimonials(t.data);
  };

  useEffect(() => { if (user) load(); }, [user]);

  const delBike = async (id) => {
    if (!window.confirm("Delete this bike?")) return;
    await authAxios().delete(`/admin/bikes/${id}`);
    toast.success("Bike deleted");
    load();
  };

  const delReview = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await authAxios().delete(`/admin/testimonials/${id}`);
    toast.success("Testimonial deleted");
    load();
  };

  const delPhoto = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    await authAxios().delete(`/admin/gallery/${id}`);
    toast.success("Photo deleted");
    load();
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("image", f);
        fd.append("caption", "");
        await authAxios().post(`/admin/gallery`, fd);
      }
      toast.success(`${files.length} photo(s) added`);
      load();
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (loading || !user) return <div className="flex min-h-screen items-center justify-center bg-[#0B0A09] text-white"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0B0A09]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-lg bg-white px-2 py-1"><img src={STORE.logo} alt="Knoble Rides" className="h-7 w-auto" /></span>
            <span className="hidden font-display text-sm font-bold text-white sm:inline">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-zinc-400 sm:inline">{user.email}</span>
            <button onClick={() => navigate("/")} className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-zinc-200 hover:bg-white/10">View Site</button>
            <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-1.5 rounded-full bg-[#8C5A32] px-4 py-2 text-sm font-semibold text-white">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex gap-2">
          <button onClick={() => setTab("bikes")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold ${tab === "bikes" ? "bg-[#8C5A32] text-white" : "bg-white text-zinc-600 ring-1 ring-[#E6D7C8]"}`} data-testid="admin-tab-bikes">
            <BikeIcon className="h-4 w-4" /> Bikes ({bikes.length})
          </button>
          <button onClick={() => setTab("gallery")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold ${tab === "gallery" ? "bg-[#8C5A32] text-white" : "bg-white text-zinc-600 ring-1 ring-[#E6D7C8]"}`} data-testid="admin-tab-gallery">
            <Images className="h-4 w-4" /> Gallery ({gallery.length})
          </button>
          <button onClick={() => setTab("reviews")} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold ${tab === "reviews" ? "bg-[#8C5A32] text-white" : "bg-white text-zinc-600 ring-1 ring-[#E6D7C8]"}`} data-testid="admin-tab-reviews">
            <MessageSquareQuote className="h-4 w-4" /> Testimonials ({testimonials.length})
          </button>
        </div>

        {tab === "bikes" && (
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold">Manage Bikes</h2>
              <button onClick={() => { setEditBike(null); setShowForm(true); }} className="btn-brown inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold" data-testid="admin-add-bike">
                <Plus className="h-4 w-4" /> Add Bike
              </button>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {bikes.map((b) => (
                <div key={b.id} className="overflow-hidden rounded-2xl border border-[#E6D7C8] bg-white">
                  <div className="aspect-[4/3] bg-[#F1ECE6]">
                    {b.image_url && <img src={resolveImg(b.image_url)} alt={b.name} className="h-full w-full object-cover" />}
                  </div>
                  <div className="p-4">
                    <span className="text-xs font-semibold text-[#8C5A32]">{b.category_name}</span>
                    <h3 className="font-display text-base font-bold">{b.name}</h3>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => { setEditBike(b); setShowForm(true); }} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#E6D7C8] px-3 py-2 text-sm font-semibold hover:bg-[#FDF8F3]" data-testid={`admin-edit-${b.id}`}>
                        <Pencil className="h-4 w-4" /> Edit
                      </button>
                      <button onClick={() => delBike(b.id)} className="inline-flex items-center justify-center rounded-lg border border-red-200 px-3 py-2 text-red-600 hover:bg-red-50" data-testid={`admin-delete-${b.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold">Manage Gallery</h2>
              <label className="btn-brown inline-flex cursor-pointer items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold" data-testid="admin-add-photo">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Photos
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} disabled={uploading} />
              </label>
            </div>
            <div className="mt-6 columns-2 gap-4 md:columns-3 lg:columns-4">
              {gallery.map((g) => (
                <div key={g.id} className="mb-4 break-inside-avoid overflow-hidden rounded-xl border border-[#E6D7C8] bg-white">
                  <img src={resolveImg(g.image_url)} alt={g.caption || "gallery"} className="w-full object-cover" />
                  <button onClick={() => delPhoto(g.id)} className="flex w-full items-center justify-center gap-1.5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50" data-testid={`admin-delete-photo-${g.id}`}>
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold">Manage Testimonials</h2>
              <button onClick={() => { setEditReview(null); setShowReview(true); }} className="btn-brown inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold" data-testid="admin-add-review">
                <Plus className="h-4 w-4" /> Add Testimonial
              </button>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((t) => (
                <div key={t.id} className="rounded-2xl border border-[#E6D7C8] bg-white p-5">
                  <div className="flex gap-0.5">
                    {[...Array(t.rating || 5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-[#8C5A32] text-[#8C5A32]" />)}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-700">"{t.quote}"</p>
                  <p className="mt-3 text-sm font-bold text-[#0B0A09]">{t.name} <span className="font-normal text-zinc-500">— {t.location}</span></p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => { setEditReview(t); setShowReview(true); }} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#E6D7C8] px-3 py-2 text-sm font-semibold hover:bg-[#FDF8F3]" data-testid={`admin-edit-review-${t.id}`}>
                      <Pencil className="h-4 w-4" /> Edit
                    </button>
                    <button onClick={() => delReview(t.id)} className="inline-flex items-center justify-center rounded-lg border border-red-200 px-3 py-2 text-red-600 hover:bg-red-50" data-testid={`admin-delete-review-${t.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {testimonials.length === 0 && <p className="mt-8 text-center text-zinc-500">No testimonials yet. Add your first one.</p>}
          </div>
        )}
      </div>

      {showForm && <BikeForm initial={editBike} onClose={() => setShowForm(false)} onSaved={load} authAxios={authAxios} />}
      {showReview && <TestimonialForm initial={editReview} onClose={() => setShowReview(false)} onSaved={load} authAxios={authAxios} />}
    </div>
  );
}
